import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaSpinner, FaFileAlt, FaClock, FaHeadset,
    FaCheckCircle, FaChevronLeft, FaChevronRight,
    FaExclamationTriangle, FaInbox
} from 'react-icons/fa';
import { format, parseISO, differenceInMinutes, formatDistanceToNowStrict, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import './SupportAdminDashboard.css';

// ─── CONSTANTES ────────────────────────────────────────────────────────────
const TABS = ['Aberto', 'Aguardando Suporte', 'Aguardando Cliente', 'Resolvido', 'Fechado'];
const ITEMS_PER_PAGE = 15;

// ─── HELPERS ───────────────────────────────────────────────────────────────

/**
 * Retorna cor/texto de SLA para exibição na tabela.
 * Considera se o chamado está resolvido, em atraso ou no prazo.
 */
const getSlaStatus = (chamado) => {
    if (!chamado.sla_prazo_resolucao) {
        return { text: 'S/ SLA', type: 'default', tooltip: 'Sem SLA definido' };
    }
    const prazo = parseISO(chamado.sla_prazo_resolucao);
    if (!isValid(prazo)) return { text: 'Erro', type: 'default', tooltip: 'Data inválida' };

    if (chamado.data_resolucao) {
        const dataRes = parseISO(chamado.data_resolucao);
        return isValid(dataRes) && dataRes <= prazo
            ? { text: 'Cumprido', type: 'ok',      tooltip: 'Resolvido dentro do prazo' }
            : { text: 'Violado',  type: 'violado',  tooltip: 'Resolvido com atraso' };
    }

    const minutosRestantes = differenceInMinutes(prazo, new Date());
    if (minutosRestantes < 0) {
        const atraso = formatDistanceToNowStrict(prazo, { locale: ptBR });
        return { text: 'Atrasado', type: 'violado', tooltip: `Atrasado há ${atraso}` };
    }
    if (minutosRestantes <= 240) {
        const restante = formatDistanceToNowStrict(prazo, { locale: ptBR });
        return { text: 'Atenção', type: 'alerta', tooltip: `Expira em ${restante}` };
    }
    const restante = formatDistanceToNowStrict(prazo, { locale: ptBR });
    return { text: 'No Prazo', type: 'ok', tooltip: `Vence em ${restante}` };
};

// ─── SUBCOMPONENTES ─────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon, color, loading }) => (
    <div className="sad-stat-card" style={{ '--accent': color }}>
        <div className="sad-stat-icon" style={{ color, backgroundColor: `${color}18` }}>
            {loading ? <FaSpinner className="icon-spin" /> : icon}
        </div>
        <div className="sad-stat-info">
            <span className="sad-stat-value">{loading ? '—' : value}</span>
            <span className="sad-stat-title">{title}</span>
        </div>
        <div className="sad-stat-bar" style={{ background: `${color}20` }}>
            <div className="sad-stat-bar-fill" style={{ background: color, width: loading ? '0%' : '100%' }} />
        </div>
    </div>
);

const Badge = ({ text, type, tooltip }) => (
    <span className={`sad-badge sad-badge-${type}`} title={tooltip}>{text || 'N/A'}</span>
);

/** Skeleton row para loading state */
const SkeletonRow = () => (
    <tr className="sad-skeleton-row">
        {[120, 280, 140, 160, 90, 80, 90].map((w, i) => (
            <td key={i}><div className="sad-skeleton" style={{ width: `${w}px` }} /></td>
        ))}
    </tr>
);

/** Componente de paginação */
const Pagination = ({ current, total, totalItems, perPage, onChange }) => {
    if (total <= 1) return null;

    const start = (current - 1) * perPage + 1;
    const end   = Math.min(current * perPage, totalItems);

    // Gera janela de páginas visíveis (máx 5)
    let pages = [];
    if (total <= 7) {
        pages = Array.from({ length: total }, (_, i) => i + 1);
    } else {
        const left  = Math.max(1, current - 2);
        const right = Math.min(total, left + 4);
        pages = Array.from({ length: right - left + 1 }, (_, i) => left + i);
    }

    return (
        <div className="sad-pagination">
            <span className="sad-pagination-info">
                Exibindo <strong>{start}–{end}</strong> de <strong>{totalItems}</strong>
            </span>
            <div className="sad-pagination-controls">
                <button
                    className="sad-page-btn nav"
                    disabled={current === 1}
                    onClick={() => onChange(current - 1)}
                    aria-label="Página anterior"
                >
                    <FaChevronLeft />
                </button>

                {pages[0] > 1 && (
                    <>
                        <button className="sad-page-btn" onClick={() => onChange(1)}>1</button>
                        {pages[0] > 2 && <span className="sad-page-ellipsis">…</span>}
                    </>
                )}

                {pages.map(p => (
                    <button
                        key={p}
                        className={`sad-page-btn ${p === current ? 'active' : ''}`}
                        onClick={() => onChange(p)}
                    >
                        {p}
                    </button>
                ))}

                {pages[pages.length - 1] < total && (
                    <>
                        {pages[pages.length - 1] < total - 1 && <span className="sad-page-ellipsis">…</span>}
                        <button className="sad-page-btn" onClick={() => onChange(total)}>{total}</button>
                    </>
                )}

                <button
                    className="sad-page-btn nav"
                    disabled={current === total}
                    onClick={() => onChange(current + 1)}
                    aria-label="Próxima página"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
};

// ─── COMPONENTE PRINCIPAL ───────────────────────────────────────────────────

export default function SupportAdminDashboard() {
    const [chamados,      setChamados]      = useState([]);
    const [stats,         setStats]         = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [loadingStats,  setLoadingStats]  = useState(true);
    const [activeTab,     setActiveTab]     = useState('Aberto');
    const [currentPage,   setCurrentPage]   = useState(1);

    // ── Multitenant (admin_geral) ─────────────────────────────────────────
    const [isAdminGeral,  setIsAdminGeral]  = useState(false);
    const [tenants,       setTenants]       = useState([]);
    const [activeTenant,  setActiveTenant]  = useState(''); // '' = contexto do próprio token

    const navigate = useNavigate();

    // ── Computed ──────────────────────────────────────────────────────────
    const totalPages       = Math.ceil(chamados.length / ITEMS_PER_PAGE);
    const paginatedChamados = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return chamados.slice(start, start + ITEMS_PER_PAGE);
    }, [chamados, currentPage]);

    // admin_geral pode enviar ?tenantId=; demais perfis caem no tenant do token (backend ignora override)
    const tenantQS = activeTenant ? `tenantId=${encodeURIComponent(activeTenant)}` : '';

    // ── Detecta perfil e carrega tenants (apenas admin_geral) ─────────────
    useEffect(() => {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const adminGeral = u?.perfil === 'admin_geral';
        setIsAdminGeral(adminGeral);
        if (adminGeral) {
            // Fonte única de verdade dos tenants: SystemHub
            api.get('/api/system-hub/clientes')
                .then(({ data }) => setTenants(Array.isArray(data) ? data : []))
                .catch(() => {/* silencioso — seletor é opcional */});
        }
    }, []);

    // ── Data fetching ─────────────────────────────────────────────────────
    const fetchStats = useCallback(async () => {
        try {
            setLoadingStats(true);
            const { data } = await api.get(`/api/admin/suporte/chamados/stats${tenantQS ? `?${tenantQS}` : ''}`);
            setStats(data);
        } catch {
            /* silencioso — stats é informação complementar */
        } finally {
            setLoadingStats(false);
        }
    }, [tenantQS]);

    const fetchChamados = useCallback(async (status) => {
        setLoading(true);
        try {
            const params = [`status=${encodeURIComponent(status)}`];
            if (tenantQS) params.push(tenantQS);
            const { data } = await api.get(`/api/admin/suporte/chamados?${params.join('&')}`);
            setChamados(data);
        } catch (err) {
            toast.error(`Erro ao carregar chamados: ${err.response?.data?.error || err.message}`);
            setChamados([]);
        } finally {
            setLoading(false);
        }
    }, [tenantQS]);

    // ── Effects ───────────────────────────────────────────────────────────
    // fetchStats muda quando o tenant ativo muda → recarrega automaticamente
    useEffect(() => { fetchStats(); }, [fetchStats]);

    useEffect(() => {
        setCurrentPage(1); // Reseta paginação ao trocar de aba (ou de tenant)
        fetchChamados(activeTab);
    }, [activeTab, fetchChamados]);

    // ── Socket: atualizações em tempo real ────────────────────────────────
    const socketRef = useRef(null);

    // Conecta socket uma única vez
    useEffect(() => {
        const socket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001', {
            transports: ['websocket'],
            reconnection: true,
        });
        socketRef.current = socket;
        return () => socket.disconnect();
    }, []);

    // Registra listeners (re-registra quando a aba ativa ou o tenant muda)
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket) return;

        /**
         * Novo chamado criado → atualiza stats e, se estamos na aba "Aberto",
         * insere o ticket no topo da lista sem re-fetch completo.
         */
        const handleNewTicket = ({ chamadoId, assunto, criado_por_nome }) => {
            fetchStats();
            if (activeTab === 'Aberto') {
                fetchChamados('Aberto');
            }
            toast.info(`📋 Novo chamado: ${assunto || 'Sem título'}`, {
                toastId:   'dashboard-new-ticket',
                autoClose: 6000,
            });
        };

        /**
         * Status de um ticket mudou.
         * — Se o ticket está na lista atual e saiu desta aba → remove do state.
         * — Se o ticket chegou nesta aba → re-fetch para exibir.
         * — Sempre atualiza as stats.
         */
        const handleStatusChanged = ({ chamadoId, newStatus }) => {
            fetchStats();
            setChamados(prev => {
                const exists = prev.find(c => String(c.id) === String(chamadoId));
                if (exists && newStatus !== activeTab) {
                    // Saiu desta aba → remove
                    return prev.filter(c => String(c.id) !== String(chamadoId));
                }
                return prev;
            });
            // Se o ticket chegou nesta aba, traz ele do servidor
            if (newStatus === activeTab) {
                fetchChamados(activeTab);
            }
        };

        socket.on('support_new_ticket',          handleNewTicket);
        socket.on('support_ticket_status_changed', handleStatusChanged);

        return () => {
            socket.off('support_new_ticket',          handleNewTicket);
            socket.off('support_ticket_status_changed', handleStatusChanged);
        };
    }, [activeTab, fetchStats, fetchChamados]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleRefresh = () => {
        fetchStats();
        fetchChamados(activeTab);
    };

    const handleTabChange = (tab) => {
        if (tab !== activeTab) setActiveTab(tab);
    };

    const handleRowClick = (chamado) => {
        navigate(`/suporte-admin/chamado/${chamado.id}`);
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="sad-container">

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <header className="sad-header">
                <div className="sad-header-text">
                    <h1>Painel de Suporte</h1>
                    <p>Visão geral e gerenciamento de tickets</p>
                </div>

                <div className="sad-header-actions">
                    {isAdminGeral && (
                        <div className="sad-tenant-switcher">
                            <span className="sad-tenant-label">Tenant</span>
                            <select
                                value={activeTenant}
                                onChange={e => setActiveTenant(e.target.value)}
                                title="Selecione o cliente para inspecionar"
                            >
                                <option value="">Meu contexto (token)</option>
                                {tenants.map(t => (
                                    <option key={t.id} value={t.id}>{t.nome_empresa}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button className="sad-refresh-btn" onClick={handleRefresh}>
                        Atualizar
                    </button>
                </div>
            </header>

            {/* ── STATS CARDS ─────────────────────────────────────────── */}
            <div className="sad-stats-grid">
                <StatCard
                    title="Em Aberto"
                    value={stats?.['Aberto'] ?? 0}
                    icon={<FaFileAlt />}
                    color="#3b82f6"
                    loading={loadingStats}
                />
                <StatCard
                    title="Em Andamento"
                    value={stats?.['Aguardando Suporte'] ?? 0}
                    icon={<FaHeadset />}
                    color="#8b5cf6"
                    loading={loadingStats}
                />
                <StatCard
                    title="Aguard. Cliente"
                    value={stats?.['Aguardando Cliente'] ?? 0}
                    icon={<FaClock />}
                    color="#f59e0b"
                    loading={loadingStats}
                />
                <StatCard
                    title="Resolvidos"
                    value={stats?.['Resolvido'] ?? 0}
                    icon={<FaCheckCircle />}
                    color="#10b981"
                    loading={loadingStats}
                />
            </div>

            {/* ── CONTENT ─────────────────────────────────────────────── */}
            <div className="sad-content">

                {/* TABS */}
                <div className="sad-tabs">
                    {TABS.map(tab => {
                        const count = stats?.[tab];
                        return (
                            <button
                                key={tab}
                                className={`sad-tab-btn ${activeTab === tab ? 'active' : ''}`}
                                onClick={() => handleTabChange(tab)}
                            >
                                {tab}
                                {count > 0 && (
                                    <span className="sad-tab-count">{count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* TABLE */}
                <div className="sad-table-container">
                    <table className="sad-table">
                        <thead>
                            <tr>
                                <th style={{ width: 120 }}>ID</th>
                                <th>Assunto</th>
                                <th>Categoria</th>
                                <th>Solicitante</th>
                                <th className="text-center" style={{ width: 110 }}>Prioridade</th>
                                <th className="text-center" style={{ width: 100 }}>SLA</th>
                                <th className="text-right" style={{ width: 130 }}>Atualizado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                            ) : paginatedChamados.length > 0 ? (
                                paginatedChamados.map(chamado => {
                                    const sla = getSlaStatus(chamado);
                                    const isFechado = chamado.status === 'Fechado';
                                    return (
                                        <tr
                                            key={chamado.id}
                                            onClick={() => handleRowClick(chamado)}
                                            className={isFechado ? 'sad-row-closed' : ''}
                                            title={isFechado ? 'Chamado fechado (somente leitura)' : ''}
                                        >
                                            <td className="sad-id-cell">
                                                <span className="sad-id-badge">#{chamado.id}</span>
                                            </td>
                                            <td>
                                                <div className="sad-subject">
                                                    <span className="sad-subject-text">{chamado.assunto}</span>
                                                    <span className="sad-type-tag">{chamado.tipo}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="sad-category">
                                                    <span>{chamado.categoria_nome || 'Geral'}</span>
                                                    {chamado.subcategoria_nome && (
                                                        <small>› {chamado.subcategoria_nome}</small>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="sad-requester">
                                                    <strong>{chamado.criado_por_nome}</strong>
                                                    <small>{chamado.nome_empresa || 'Interno'}</small>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <Badge
                                                    text={chamado.prioridade}
                                                    type={`prio-${chamado.prioridade?.toLowerCase()}`}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <Badge text={sla.text} type={`sla-${sla.type}`} tooltip={sla.tooltip} />
                                            </td>
                                            <td className="text-right sad-date">
                                                {chamado.atualizado_em
                                                    ? format(parseISO(chamado.atualizado_em), 'dd/MM HH:mm')
                                                    : '—'}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="sad-empty">
                                            <FaInbox size={36} />
                                            <strong>Nenhum chamado encontrado</strong>
                                            <span>Esta fila está vazia no momento.</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {!loading && chamados.length > 0 && (
                    <Pagination
                        current={currentPage}
                        total={totalPages}
                        totalItems={chamados.length}
                        perPage={ITEMS_PER_PAGE}
                        onChange={setCurrentPage}
                    />
                )}
            </div>
        </div>
    );
}